import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { catchError, debounceTime, distinctUntilChanged, of, switchMap, tap, timeout, timer } from 'rxjs';
import { CcNumbersService } from '../cc-numbers.service';
import {
  ADDRESS_CHECK_BLOCKING_VERDICTS,
  AddressCheckComponents,
  AddressCheckRequest,
  AddressCheckResponse,
  RegulationAddressRequirements,
} from '../cc-numbers.model';
import { HlmAlertComponent, HlmBadgeComponent, HlmBtnDirective, HlmIconComponent, HlmSpinnerComponent } from '../../../ui';

/** Address as collected by the regulation form, before it is mapped onto the check request. */
export interface AddressCheckInput {
  streetName: string;
  buildingNumber: string;
  buildingLetter: string;
  zipCode: string;
  city: string;
}

export type AddressCheckStatus = 'inactive' | 'incomplete' | 'checking' | 'done';

export interface AddressCheckState {
  status: AddressCheckStatus;
  response: AddressCheckResponse | null;
  /** Operator accepted the geocoder-corrected address (ADDRESS_NORMALIZED only). */
  confirmed: boolean;
  /** Our own endpoint failed (network/5xx) — treated fail-open, same as PROVIDER_UNAVAILABLE. */
  endpointFailed: boolean;
}

/**
 * Single source of truth for "does this state prevent a submission?".
 *
 * The component's own `blocked()` and every dialog hosting the check call this — the rule used to
 * be written out in all three places, so adding a verdict to ADDRESS_CHECK_BLOCKING_VERDICTS would
 * have left the dialogs silently permissive.
 *
 * Callers still decide *whether the check applies at all* (country, required address fields, a
 * known city prefix); this function only judges a state that is in scope.
 */
export function isAddressCheckBlocking(state: AddressCheckState | null | undefined): boolean {
  if (!state) return false;
  if (state.status === 'inactive') return false;
  // No verdict yet (incomplete address, or a check in flight) — never book on an unjudged address.
  if (state.status !== 'done') return true;
  // Fail-open: our own endpoint failing must not stop regulated German bookings.
  if (state.endpointFailed) return false;

  const verdict = state.response?.verdict;
  if (!verdict) return true;
  if (ADDRESS_CHECK_BLOCKING_VERDICTS.includes(verdict)) return true;
  if (verdict === 'ADDRESS_NORMALIZED') return !state.confirmed;
  return false;
}

/**
 * The address-check states in which a host's submit button is DISABLED rather than clickable:
 * the check is running, or it returned a blocking verdict the operator must act on in the check
 * panel. An `incomplete` address is not one of them — missing fields are revealed on click.
 * Shared by the booking and fix-regulation dialogs so the two gates cannot drift apart.
 * @ticket EB-3809
 */
export function addressCheckDisablesSubmit(state: AddressCheckState | null | undefined): boolean {
  if (!state) return false;
  if (state.status === 'checking') return true;
  return state.status === 'done' && isAddressCheckBlocking(state);
}

/**
 * Footer explainer for a submit disabled by the address check (`addressCheckDisablesSubmit`);
 * null in every other state. `action` completes "Confirm the corrected address to …".
 * @ticket EB-3809
 */
export function addressCheckBlockReason(
  state: AddressCheckState | null | undefined,
  action: 'enable booking' | 're-submit',
): string | null {
  if (!state || !addressCheckDisablesSubmit(state)) return null;
  if (state.status === 'checking') return 'Verifying address…';
  const verdict = state.response?.verdict;
  if (verdict === 'ADDRESS_NORMALIZED') return `Confirm the corrected address to ${action}`;
  if (verdict === 'AREACODE_MISMATCH') return 'Address is in a different area code';
  if (verdict === 'NOT_FOUND') return 'Address could not be verified';
  return 'Address could not be checked — see the address check above';
}

/** Address fields the check needs to produce a verdict. */
const REQUIRED_ADDRESS_FIELDS = ['streetName', 'buildingNumber', 'zipCode', 'city'] as const;

/**
 * Single source of truth for "does the check apply to this city at all?" — the companion to
 * `isAddressCheckBlocking`, which only judges a state already known to be in scope. Both dialogs
 * hosting the check call this; the rule used to be written out in each of them.
 *
 * `addressRequirements` is a three-state map, not a list of field names:
 *   `true`    the city demands the field,
 *   `false`   the city offers the field but the operator may leave it blank
 *             (`buildingLetter` ships this way today, so the state is real and in use),
 *   absent    the city does not know the field.
 *
 * Only `true` may arm the check. Arming on mere presence (PS-6513) let the operator leave an
 * optional field blank — which the form permits — so the address snapshot stayed incomplete, no
 * verdict was ever produced, and the submit button was disabled for good with "Complete the
 * address to enable booking". The only way out was filling a field the regulation does not ask for.
 */
export function addressCheckAppliesTo(requirements: RegulationAddressRequirements | null | undefined): boolean {
  if (!requirements) return false;
  return REQUIRED_ADDRESS_FIELDS.every(field => requirements[field] === true);
}

// Geoapify sits on a rate-limited package (no 5 req/s tier), and every check is one geocode call
// plus one WFS lookup upstream. The frontend therefore spends calls deliberately:
//   1. a long debounce, so typing a field never bills a call per keystroke,
//   2. a per-dialog cache, so an address already judged is never asked about twice,
//   3. a floor between two live calls, so no burst can leave this dialog.
const DEBOUNCE_MS = 1200;
const MIN_INTERVAL_BETWEEN_CALLS_MS = 2000;
/** Safety net only — phoneservice caps its own upstream at 10 s (readTimeout) and answers
 *  PROVIDER_UNAVAILABLE itself. Cutting earlier would throw away verdicts still on their way. */
const REQUEST_TIMEOUT_MS = 15000;

/**
 * Geocode address check for the booking dialog (PS-6489).
 *
 * Runs automatically — there is no button the operator has to press. Because every check is a
 * rate-limited third-party geocode call, it only fires once the address is complete, debounced,
 * and never twice for the same payload.
 *
 * Blocking is deliberately asymmetric (PS-6483, 2026-08-20): a definite negative
 * (AREACODE_MISMATCH / NOT_FOUND) blocks booking, an unreachable provider does not — a provider
 * outage must not stop all regulated German bookings.
 */
@Component({
  selector: 'cmt-address-check',
  standalone: true,
  imports: [HlmAlertComponent, HlmBadgeComponent, HlmBtnDirective, HlmIconComponent, HlmSpinnerComponent],
  template: `
    @if (state().status !== 'inactive') {
      <div>
        @switch (view()) {

          @case ('incomplete') {
            <hlm-alert tone="info">
              <hlm-icon name="lucideMapPin" size="0.95rem" class="mt-px text-primary" />
              <div>
                <div class="font-medium">Address check pending</div>
                <div class="text-muted-foreground">
                  Fill in street, house number, ZIP and city — the address is then verified automatically.
                </div>
              </div>
            </hlm-alert>
          }

          @case ('checking') {
            <hlm-alert tone="info">
              <hlm-spinner size="16" class="mt-px" />
              <div>
                <div class="font-medium">Verifying address…</div>
                <div class="text-muted-foreground">Checking the address against the geocoder and the official area-code map.</div>
              </div>
            </hlm-alert>
          }

          @case ('match') {
            <hlm-alert tone="success">
              <hlm-icon name="lucideCircleCheck" size="0.95rem" class="mt-px text-success" />
              <div class="min-w-0 flex-1">
                <div class="flex flex-wrap items-center gap-2">
                  <span class="font-medium">Address verified</span>
                  @if (areaCodeVerified()) {
                    <hlm-badge variant="success">Area code {{ areaCodeDisplay() }}</hlm-badge>
                  }
                </div>
                @if (response()?.normalizedAddress) {
                  <div class="truncate font-mono-feat text-muted-foreground">{{ response()!.normalizedAddress }}</div>
                }
                @if (!areaCodeVerified()) {
                  <div class="text-muted-foreground">
                    Address confirmed, but the area code was not cross-checked for this number type.
                  </div>
                }
              </div>
            </hlm-alert>
          }

          @case ('normalized') {
            <hlm-alert tone="warn">
              <hlm-icon name="lucideTriangleAlert" size="0.95rem" class="mt-px text-accent-foreground" />
              <div class="min-w-0 flex-1">
                <div class="font-medium">This address was corrected</div>
                <div class="text-muted-foreground">
                  @if (changedFieldsLabel(); as fields) {
                    The geocoder corrected the <strong class="text-foreground">{{ fields }}</strong>.
                  }
                  Booking uses the corrected address. Confirm it, or change the address fields above.
                </div>

                <div class="mt-2 grid gap-2 sm:grid-cols-2">
                  <div class="min-w-0 rounded-md border border-border bg-background/60 px-2 py-1.5">
                    <div class="cmt-eyebrow">Entered</div>
                    <div class="break-words text-base">{{ enteredAddress() }}</div>
                  </div>
                  <div class="min-w-0 rounded-md border border-success/40 bg-success/5 px-2 py-1.5">
                    <div class="cmt-eyebrow !text-success">Corrected</div>
                    <div class="break-words text-base">{{ response()?.normalizedAddress ?? '—' }}</div>
                  </div>
                </div>

                @if (state().confirmed) {
                  <div class="mt-2 flex items-center gap-1.5 text-success">
                    <hlm-icon name="lucideCircleCheck" size="0.85rem" />
                    <span class="font-medium">Corrected address confirmed</span>
                  </div>
                } @else {
                  <button hlmBtn size="sm" class="mt-2" type="button" (click)="confirmCorrection()">
                    <hlm-icon name="lucideCheck" size="0.85rem" />
                    Apply corrected address
                  </button>
                }
              </div>
            </hlm-alert>
          }

          @case ('areacodeMismatch') {
            <hlm-alert tone="error">
              <hlm-icon name="lucideCircleX" size="0.95rem" class="mt-px text-destructive" />
              <div class="min-w-0 flex-1">
                <div class="font-medium">Address is in a different area code</div>
                <div class="text-muted-foreground">
                  The number would be booked for
                  <strong class="text-foreground">{{ response()?.requestedCityPrefix }}</strong>, but this address
                  geocodes into <strong class="text-foreground">{{ areaCodeDisplay() }}</strong>.
                  Pick the matching city, or correct the address.
                </div>
              </div>
            </hlm-alert>
          }

          @case ('notFound') {
            <hlm-alert tone="error">
              <hlm-icon name="lucideCircleX" size="0.95rem" class="mt-px text-destructive" />
              <div class="min-w-0 flex-1">
                <div class="font-medium">Address could not be found</div>
                <div class="text-muted-foreground">
                  The geocoder cannot pin this address to a building. Check street, house number, ZIP and city —
                  a missing house number is the most common cause.
                </div>
              </div>
            </hlm-alert>
          }

          @case ('unavailable') {
            <hlm-alert tone="neutral">
              <hlm-icon name="lucideWifiOff" size="0.95rem" class="mt-px text-muted-foreground" />
              <div class="min-w-0 flex-1">
                <div class="font-medium">Address not verified</div>
                <div class="text-muted-foreground">
                  The address service is unreachable, so this address was not checked. Booking is still possible.
                </div>
              </div>
            </hlm-alert>
          }
        }
      </div>
    }
  `,
})
export class AddressCheckComponent {
  private readonly ccNumbersService = inject(CcNumbersService);
  private readonly destroyRef = inject(DestroyRef);

  /** ISO-A3 of the selected country. Non-DEU answers NOT_APPLICABLE server-side. */
  readonly isoCountryCode = input.required<string>();
  /** City prefix the number would be booked for — the ONKZ the geocoded result is compared against. */
  readonly cityPrefix = input.required<string>();
  readonly address = input.required<AddressCheckInput | null>();
  /** False when the city needs no regulation address at all — the check is then not shown. */
  readonly enabled = input(true);

  readonly stateChange = output<AddressCheckState>();
  /** Emitted when the operator adopts the correction — carries the fields to write into the form. */
  readonly correctionApplied = output<AddressCheckComponents>();

  /** Verdicts already obtained in this dialog, keyed by the exact request payload. Bounded by how
   *  many distinct addresses one operator types into one booking — no eviction needed. */
  private readonly cache = new Map<string, AddressCheckResponse>();
  private lastCallAt = 0;

  private readonly _state = signal<AddressCheckState>({
    status: 'inactive',
    response: null,
    confirmed: false,
    endpointFailed: false,
  });
  readonly state = this._state.asReadonly();
  readonly response = computed(() => this._state().response);

  /** True while the operator must not book: a definite negative, an unconfirmed correction,
   *  or a check that has not produced a verdict yet. */
  readonly blocked = computed(() => isAddressCheckBlocking(this._state()));

  /** Request payload, or null while the address is not complete enough to spend a geocode call on. */
  private readonly payload = computed<AddressCheckRequest | null>(() => {
    if (!this.enabled()) return null;
    const a = this.address();
    if (!a) return null;
    const street = a.streetName?.trim() ?? '';
    const zipCode = a.zipCode?.trim() ?? '';
    const city = a.city?.trim() ?? '';
    // Geocode has ONE house-number field, the form has two.
    const buildingNumber = `${a.buildingNumber?.trim() ?? ''}${a.buildingLetter?.trim() ?? ''}`;
    if (!street || !zipCode || !city || !buildingNumber) return null;
    return { street, buildingNumber, zipCode, city };
  });

  /** Stable identity of a request — the dedupe key that prevents paying twice for one address. */
  private readonly payloadKey = computed(() => {
    const p = this.payload();
    if (!p || !this.isoCountryCode() || !this.cityPrefix()) return '';
    return JSON.stringify([this.isoCountryCode(), this.cityPrefix(), p]);
  });

  readonly enteredAddress = computed(() => {
    const p = this.payload();
    return p ? `${p.street} ${p.buildingNumber}, ${p.zipCode} ${p.city}` : '';
  });

  /** Area code as the operator reads it: the backend sends the official ONKZ with a leading zero
   *  ("030") while the requested city prefix has none ("30"). Showing both raw reads as a mismatch. */
  readonly areaCodeDisplay = computed(() => {
    const code = this.response()?.areaCode;
    return code ? code.replace(/^0+/, '') : null;
  });

  /** True only when the ONKZ cross-check actually ran. National numbers (32/3222) skip it, and so
   *  does every not-found address — claiming a verified area code there would be a false statement. */
  readonly areaCodeVerified = computed(() => {
    const r = this.response();
    return !!r?.areaCodeChecked && !!r.areaCode;
  });

  /** Human-readable list of what the geocoder changed, e.g. "street and ZIP code". */
  readonly changedFieldsLabel = computed(() => {
    const labels: Record<string, string> = {
      street: 'street', buildingNumber: 'house number', zipCode: 'ZIP code', city: 'city',
    };
    const fields = (this.response()?.mismatchedFields ?? [])
      .filter(f => f !== 'areaCode')
      .map(f => labels[f] ?? f);
    if (!fields.length) return null;
    if (fields.length === 1) return fields[0];
    return `${fields.slice(0, -1).join(', ')} and ${fields[fields.length - 1]}`;
  });

  /** One switch key per rendered state, so the template holds no verdict logic. */
  readonly view = computed(() => {
    const s = this._state();
    if (s.status === 'incomplete') return 'incomplete';
    if (s.status === 'checking') return 'checking';
    if (s.endpointFailed) return 'unavailable';
    switch (s.response?.verdict) {
      case 'MATCH': return 'match';
      case 'ADDRESS_NORMALIZED': return 'normalized';
      case 'AREACODE_MISMATCH': return 'areacodeMismatch';
      case 'NOT_FOUND': return 'notFound';
      case 'PROVIDER_UNAVAILABLE': return 'unavailable';
      // NOT_APPLICABLE and anything the backend may add later: render nothing rather than fall
      // through to "pending", which would claim a check is still running while booking is (rightly)
      // allowed. Unreachable today — non-DEU disables the component — but a wrong statement is the
      // one thing this box must never make.
      default: return 'hidden';
    }
  });

  constructor() {
    // Any address edit invalidates the previous verdict immediately — a stale MATCH must never
    // gate an address the operator has since changed.
    effect(() => {
      const key = this.payloadKey();
      const enabled = this.enabled();
      untracked(() => {
        const next: AddressCheckState = !enabled
          ? { status: 'inactive', response: null, confirmed: false, endpointFailed: false }
          : key
            ? { status: 'checking', response: null, confirmed: false, endpointFailed: false }
            : { status: 'incomplete', response: null, confirmed: false, endpointFailed: false };
        this.setState(next);
      });
    });

    toObservable(this.payloadKey)
      .pipe(
        debounceTime(DEBOUNCE_MS),
        distinctUntilChanged(),
        switchMap(key => {
          if (!key) return of(null);

          // Already judged in this dialog (operator went back and forth, or re-typed the same
          // value): reuse it. Costs nothing upstream.
          const cached = this.cache.get(key);
          if (cached) return of(cached);

          const [iso, prefix, payload] = JSON.parse(key) as [string, string, AddressCheckRequest];
          const sinceLast = Date.now() - this.lastCallAt;
          const wait = Math.max(0, MIN_INTERVAL_BETWEEN_CALLS_MS - sinceLast);

          return timer(wait).pipe(
            switchMap(() => {
              this.lastCallAt = Date.now();
              return this.ccNumbersService.checkAddress(iso, prefix, payload).pipe(
                timeout(REQUEST_TIMEOUT_MS),
                // Cache definite verdicts only. PROVIDER_UNAVAILABLE is transient — caching it
                // would freeze a timeout for the dialog's lifetime and make a retry impossible,
                // which is exactly what the contract asks for on that verdict. It also hits the
                // addresses that need a retry most: common street names time out upstream.
                tap(res => {
                  if (res.verdict !== 'PROVIDER_UNAVAILABLE') this.cache.set(key, res);
                }),
                catchError(() => of('ENDPOINT_FAILED' as const)),
              );
            }),
          );
        }),
        tap(result => {
          if (result === null) return;
          if (result === 'ENDPOINT_FAILED') {
            // Fail-open, consistent with PROVIDER_UNAVAILABLE: our own endpoint being down must
            // not block bookings either.
            this.setState({ status: 'done', response: null, confirmed: false, endpointFailed: true });
            return;
          }
          this.setState({ status: 'done', response: result, confirmed: false, endpointFailed: false });
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  confirmCorrection(): void {
    const s = this._state();
    if (s.response?.verdict !== 'ADDRESS_NORMALIZED') return;
    this.setState({ ...s, confirmed: true });

    // Write the corrected values into the form. The host patches silently, so this does not come
    // back as an address change — the confirmed verdict stays valid and costs no second call.
    const components = s.response.normalizedComponents;
    if (components) this.correctionApplied.emit(components);
  }

  private setState(next: AddressCheckState): void {
    this._state.set(next);
    this.stateChange.emit(next);
  }
}
